import axios from 'axios';
import store from '../store';
import { refreshToken, logoutUser } from '../store/slices/authSlice';
import { API_CONFIG } from '../constants/apiConfig';

const PUBLIC_ROUTES = [
  '/auth/login',
  '/auth/register',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/organizations',
  '/auth/roles',
];

const isPublicRoute = (url) => {
  if (!url) return false;
  return PUBLIC_ROUTES.some(route => url.includes(route));
};

const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// ✅ Request interceptor - Skip token for public routes
apiClient.interceptors.request.use(
  (config) => {
    // Skip adding token for public routes
    if (isPublicRoute(config.url)) {
      console.log('🔓 Public route - skipping token:', config.url);
      return config;
    }

    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// ✅ Response interceptor - Skip token refresh for public routes
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // ✅ Skip token refresh for public routes
    if (isPublicRoute(originalRequest?.url)) {
      console.log('🔓 Public route - skipping token refresh');
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized - Token expired
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshTokenStr = localStorage.getItem('refresh_token');
        
        if (!refreshTokenStr) {
          console.log('❌ No refresh token available');
          await store.dispatch(logoutUser());
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
          return Promise.reject(new Error('No refresh token available'));
        }

        const result = await store.dispatch(refreshToken()).unwrap();
        
        if (result.token) {
          localStorage.setItem('access_token', result.token);
          originalRequest.headers.Authorization = `Bearer ${result.token}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        console.error('❌ Token refresh failed:', refreshError);
        await store.dispatch(logoutUser());
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;